import { doFetch, FetchResult as InnerFetchResult } from "./doFetch";
import { GameState, Guess, Ray, Leaders, PagedResult, GameSummaries } from "../types/GameTypes";

export type FetchResult<T> = InnerFetchResult<T>;

const API = process.env.REACT_APP_API_URL;

export async function startGame(
  player: string,
  ballCount: number,
  setResult: (data: FetchResult<GameState>) => void
): Promise<FetchResult<GameState>> {
  return await doFetch(
    `${API}/games`,
    {
      captureErrors: true,
      timeout: 20000,
      setResult,
    },
    {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
      },
      referrerPolicy: "no-referrer",
      body: JSON.stringify({
        player,
        ballCount,
      }),
    }
  );
}

export async function fetchGame(
  nonce: string,
  setResult?: React.Dispatch<React.SetStateAction<FetchResult<GameState>>>
): Promise<FetchResult<GameState>> {
  return await doFetch(`${API}/games/${nonce}`, {
    captureErrors: true,
    captureStatus: true,
    timeout: 20000,
    setResult,
  });
}

export async function putGuessAt(nonce: string, x: number, y: number): Promise<FetchResult<Guess>> {
  return await doFetch(
    `${API}/games/${nonce}/guesses`,
    {
      captureErrors: true,
      captureStatus: true,
    },
    {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
      },
      referrerPolicy: "no-referrer",
      body: JSON.stringify({ x, y }), // body data type must match "Content-Type" header
    }
  );
}

export async function deleteGuessAt(nonce: string, x: number, y: number): Promise<FetchResult<Guess>> {
  return await doFetch(
    `${API}/games/${nonce}/guesses/${x},${y}`,
    {
      captureErrors: true,
      captureStatus: true,
    },
    {
      method: "DELETE",
      mode: "cors",
      cache: "no-cache",
      referrerPolicy: "no-referrer",
    }
  );
}

export async function shootRay(
  nonce: string,
  entryPortOrdinal: number,
  setResult: React.Dispatch<React.SetStateAction<FetchResult<Ray>>>
): Promise<FetchResult<Ray>> {
  return await doFetch(
    `${API}/games/${nonce}/rays`,
    {
      setResult,
      loadingDelay: 0,
      captureErrors: true,
      captureStatus: true,
    },
    {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
      },
      referrerPolicy: "no-referrer",
      body: `${entryPortOrdinal}`,
    }
  );
}

export async function submitGuesses(nonce: string): Promise<FetchResult<GameState>> {
  return await doFetch(
    `${API}/games/${nonce}/submit`,
    {
      captureErrors: true,
      captureStatus: true,
      timeout: 20000,
    },
    {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      referrerPolicy: "no-referrer",
    }
  );
}

export async function fetchLeaders(
  setResult: React.Dispatch<React.SetStateAction<FetchResult<Leaders>>>
): Promise<FetchResult<Leaders>> {
  return await doFetch(`${API}/leaders`, {
    captureErrors: false,
    captureStatus: false,
    setResult,
    loadingDelay: 1000,
  });
}

export async function fetchRecentGames(
  count: number,
  setResult: React.Dispatch<React.SetStateAction<FetchResult<GameSummaries>>>
): Promise<FetchResult<GameSummaries>> {
  return await doFetch(`${API}/recent-games?count=${count}`, {
    captureErrors: false,
    captureStatus: false,
    setResult,
    loadingDelay: 1000,
  });
}

export async function fetchPlayerGames(
  player: string,
  pageNumber: number,
  pageSize: number,
  setResult: React.Dispatch<React.SetStateAction<FetchResult<PagedResult<GameSummaries>>>>
): Promise<FetchResult<PagedResult<GameSummaries>>> {
  return await doFetch(`${API}/player-games/${player}?page=${pageNumber}&size=${pageSize}`, {
    captureErrors: false,
    captureStatus: false,
    setResult,
    loadingDelay: 1000,
  });
}
